import { ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { UnitTeamDropdown } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import { SettingsV2PageTitle } from "../companySettingsV2.styled";
import {
  OrganizationCreateDialog,
  OrganizationEditPanel,
  OrganizationMembersPanel,
} from "./organizationSettings.dialogs.component";
import type {
  OrganizationEditOperation,
  OrganizationEditTarget,
} from "./organizationSettings.dialogs.component";
import { EntityTabs } from "./organizationSettings.entityTabs.component";
import {
  filterOrganizationUnits,
  flattenOrganizationUnits,
  getEntityDisplayName,
  getTeamMemberCounts,
  getUnitPersonCount,
  getUnitTeamCount,
  resolveOrganizationFilterSelection,
} from "./organizationSettings.logic";
import type { OrganizationUnit } from "./organizationSettings.logic";
import {
  AddTeamRow,
  EmptyState,
  EmptyText,
  EmptyTitle,
  EntityNavigation,
  EntityNavigationTitle,
  ErrorBox,
  ErrorRow,
  Header,
  HeaderCopy,
  IconButton,
  Intro,
  LoadingText,
  Meta,
  Page,
  RowActions,
  TeamCount,
  TeamList,
  TeamName,
  TeamRow,
  UnitCard,
  UnitControls,
  UnitHeader,
  UnitList,
  UnitTitle,
  UnitTitleRow,
  UnitTitleWrap,
} from "./organizationSettings.styled";
import { useOrganizationSettings } from "./useOrganizationSettings.hook";

type CreateTarget =
  | { readonly kind: "entity" }
  | { readonly entityId: number; readonly kind: "unit" }
  | {
      readonly kind: "team";
      readonly unitId: number;
      readonly unitName: string;
    }
  | null;

export const OrganizationSettings = (): ReactElement => {
  const { t } = useTranslation();
  const organization = useOrganizationSettings();
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null);
  const [expandedUnits, setExpandedUnits] = useState<number[]>([]);
  const [createTarget, setCreateTarget] = useState<CreateTarget>(null);
  const [editTarget, setEditTarget] = useState<OrganizationEditTarget | null>(
    null,
  );
  const [membersTeamId, setMembersTeamId] = useState<number | null>(null);
  const hasOrganizationFilter =
    selectedTeamIds.length > 0 || selectedUnitId != null;
  const entities = organization.structure.entities;
  const activeEntityId = entities.some(
    (entity) => entity.id === selectedEntityId,
  )
    ? selectedEntityId
    : (entities[0]?.id ?? null);
  const selectedEntity = entities.find(
    (entity) => entity.id === activeEntityId,
  );

  const units = useMemo(
    () =>
      selectedEntity
        ? flattenOrganizationUnits({ entities: [selectedEntity] })
        : [],
    [selectedEntity],
  );
  const organizationFilterOptions = useMemo(
    () =>
      entities.flatMap((entity) =>
        entity.units.map((unit) => ({
          entityId: entity.id,
          entityName: getEntityDisplayName({
            name: entity.name,
            unhandledName: t("OrganizationSettings.Unhandled"),
          }),
          unitId: unit.id,
          unitName: unit.name,
          teams: unit.workGroups.map((team) => ({
            id: team.id,
            name: team.name,
          })),
        })),
      ),
    [entities, t],
  );
  const entityFilterOptions = useMemo(
    () =>
      entities.map((entity) => ({
        id: entity.id,
        name: getEntityDisplayName({
          name: entity.name,
          unhandledName: t("OrganizationSettings.Unhandled"),
        }),
      })),
    [entities, t],
  );

  useEffect(() => {
    setExpandedUnits((current) => {
      const next = units.map((unit) => unit.id);
      return current.length === 0
        ? next
        : Array.from(new Set([...current, ...next]));
    });
  }, [units]);

  useEffect(() => {
    const availableTeamIds = new Set(
      units.flatMap((unit) => unit.workGroups.map((team) => String(team.id))),
    );
    setSelectedTeamIds((current) =>
      current.filter((teamId) => availableTeamIds.has(teamId)),
    );
    setSelectedUnitId((current) =>
      current == null || units.some((unit) => unit.id === current)
        ? current
        : null,
    );
  }, [units]);

  const visibleUnits = useMemo(
    () => filterOrganizationUnits({ selectedTeamIds, selectedUnitId, units }),
    [selectedTeamIds, selectedUnitId, units],
  );
  const teamMemberCounts = useMemo(
    () =>
      organization.workers == null
        ? null
        : getTeamMemberCounts(organization.workers),
    [organization.workers],
  );

  const handleOrganizationFilterChange = (teamIds: string[]): void => {
    setSelectedUnitId(null);
    const selection = resolveOrganizationFilterSelection({
      activeEntityId,
      currentTeamIds: selectedTeamIds,
      nextTeamIds: teamIds,
      options: organizationFilterOptions,
    });
    if (selection.entityId !== activeEntityId) {
      setSelectedEntityId(selection.entityId);
    }
    setSelectedTeamIds(selection.teamIds);
  };

  const handleUnitFilterChange = (unitId: number | null): void => {
    if (unitId != null) {
      const entityId = organizationFilterOptions.find(
        (option) => option.unitId === unitId,
      )?.entityId;
      if (entityId != null) setSelectedEntityId(entityId);
    }
    setSelectedTeamIds([]);
    setSelectedUnitId(unitId);
  };

  const toggleUnit = (unitId: number): void => {
    setExpandedUnits((current) =>
      current.includes(unitId)
        ? current.filter((id) => id !== unitId)
        : [...current, unitId],
    );
  };

  const renderUnit = (unit: OrganizationUnit): ReactElement => {
    const isExpanded = expandedUnits.includes(unit.id);
    const teamCount = unit.workGroups.length;
    const personCount = getUnitPersonCount({
      unit,
      workers: organization.workers,
    });

    return (
      <UnitCard key={unit.id}>
        <UnitHeader>
          <UnitTitleWrap>
            <UnitTitleRow>
              <UnitTitle>{unit.name}</UnitTitle>
              <TooltipHover text={t("OrganizationSettings.UnitSettings")}>
                <IconButton
                  type="button"
                  aria-label={t("OrganizationSettings.EditUnit", {
                    name: unit.name,
                  })}
                  onClick={() =>
                    setEditTarget({
                      kind: "unit",
                      id: unit.id,
                      entityId: unit.entityId,
                      name: unit.name,
                      teamCount: getUnitTeamCount({
                        entityId: unit.entityId,
                        unitId: unit.id,
                        units,
                      }),
                    })
                  }
                >
                  <CustomIcon name="v2/pen" size="18px" color={ColorV2.Blue1} />
                </IconButton>
              </TooltipHover>
            </UnitTitleRow>
            <Meta>
              {t("OrganizationSettings.UnitTeamCount", {
                count: teamCount,
              })}
              {", "}
              {t("OrganizationSettings.UnitPersonCount", {
                count: personCount,
              })}
            </Meta>
          </UnitTitleWrap>
          <IconButton
            type="button"
            aria-expanded={isExpanded}
            aria-label={
              isExpanded
                ? t("OrganizationSettings.CollapseUnit")
                : t("OrganizationSettings.ExpandUnit")
            }
            onClick={() => toggleUnit(unit.id)}
          >
            <CustomIcon
              name={isExpanded ? "v2/up-chevron" : "v2/down-chevron"}
              size="20px"
              color={ColorV2.Blue1}
            />
          </IconButton>
        </UnitHeader>
        {isExpanded && (
          <>
            {teamCount > 0 ? (
              <TeamList>
                {unit.workGroups.map((team) => {
                  const memberCount =
                    teamMemberCounts == null
                      ? (team.workers?.length ?? 0)
                      : (teamMemberCounts.get(team.id) ?? 0);
                  return (
                    <TeamRow key={team.id}>
                      <TeamName>{team.name}</TeamName>
                      <TeamCount>
                        {t("OrganizationSettings.MemberCount", {
                          count: memberCount,
                        })}
                      </TeamCount>
                      <RowActions>
                        <TooltipHover
                          text={t("OrganizationSettings.TeamSettings")}
                        >
                          <IconButton
                            type="button"
                            aria-label={t("OrganizationSettings.EditTeam", {
                              name: team.name,
                            })}
                            onClick={() =>
                              setEditTarget({
                                kind: "team",
                                id: team.id,
                                memberCount,
                                name: team.name,
                              })
                            }
                          >
                            <CustomIcon
                              name="v2/pen"
                              size="20px"
                              color={ColorV2.Blue1}
                            />
                          </IconButton>
                        </TooltipHover>
                        <TooltipHover
                          text={t("OrganizationSettings.ManageMembers")}
                        >
                          <IconButton
                            type="button"
                            aria-label={t(
                              "OrganizationSettings.ManageTeamMembers",
                              { name: team.name },
                            )}
                            onClick={() => setMembersTeamId(team.id)}
                          >
                            <CustomIcon
                              name="v2/person"
                              size="20px"
                              color={ColorV2.Blue1}
                            />
                          </IconButton>
                        </TooltipHover>
                      </RowActions>
                    </TeamRow>
                  );
                })}
                <AddTeamRow>
                  <SecondaryButton
                    size="small"
                    iconName="v2/plus"
                    label={t("OrganizationSettings.AddTeam")}
                    onClick={() =>
                      setCreateTarget({
                        kind: "team",
                        unitId: unit.id,
                        unitName: unit.name,
                      })
                    }
                  />
                </AddTeamRow>
              </TeamList>
            ) : (
              <EmptyState>
                <EmptyTitle>
                  {t("OrganizationSettings.UnitHasNoTeams")}
                </EmptyTitle>
                <EmptyText>
                  {t("OrganizationSettings.UnitHasNoTeamsInfo")}
                </EmptyText>
                <SecondaryButton
                  size="small"
                  iconName="v2/plus"
                  label={t("OrganizationSettings.CreateTeam")}
                  onClick={() =>
                    setCreateTarget({
                      kind: "team",
                      unitId: unit.id,
                      unitName: unit.name,
                    })
                  }
                />
              </EmptyState>
            )}
          </>
        )}
      </UnitCard>
    );
  };

  const saveEdit = ({ name, target }: OrganizationEditOperation) => {
    if (target.kind === "entity") {
      return organization.updateEntity({ entityId: target.id, name });
    }
    if (target.kind === "team") {
      return organization.updateTeam({ name, teamId: target.id });
    }
    return organization.updateUnit({
      entityId: target.entityId,
      name,
      unitId: target.id,
    });
  };

  const deleteEdit = (target: OrganizationEditTarget) => {
    if (target.kind === "entity") {
      return organization.deleteEntity(target.id);
    }
    if (target.kind === "team") return organization.deleteTeam(target.id);
    return organization.deleteUnit({
      entityId: target.entityId,
      unitId: target.id,
    });
  };

  return (
    <Page>
      <Header>
        <HeaderCopy>
          <SettingsV2PageTitle>
            {t("OrganizationSettings.Title")}
          </SettingsV2PageTitle>
          <Intro>{t("OrganizationSettings.Intro")}</Intro>
        </HeaderCopy>
      </Header>

      <EntityNavigation>
        <EntityNavigationTitle>
          {t("OrganizationSettings.Entities")}
        </EntityNavigationTitle>
        {entities.length > 0 && activeEntityId != null ? (
          <EntityTabs
            entities={entities}
            selectedEntityId={activeEntityId}
            onEdit={(entity) =>
              setEditTarget({
                id: entity.id,
                kind: "entity",
                name: entity.name,
              })
            }
            onSelect={(entityId) => {
              setSelectedTeamIds([]);
              setSelectedUnitId(null);
              setSelectedEntityId(entityId);
            }}
          />
        ) : null}
        {entities.length > 0 && (
          <PrimaryButton
            size="small"
            iconName="v2/plus"
            aria-label={t("OrganizationSettings.CreateEntity")}
            disabled={organization.isLoading || organization.error != null}
            onClick={() => setCreateTarget({ kind: "entity" })}
          />
        )}
      </EntityNavigation>

      {activeEntityId != null && (
        <UnitControls>
          <UnitTeamDropdown
            entityOptions={entityFilterOptions}
            placeholder={t("OrganizationSettings.SearchEntityUnitTeam")}
            emptyText={t("OrganizationSettings.NoEntityUnitTeamMatches")}
            options={organizationFilterOptions}
            selectedEntityId={activeEntityId}
            selectedTeamIds={selectedTeamIds}
            selectedUnitId={selectedUnitId}
            onSelectedEntityIdChange={(entityId) => {
              setSelectedTeamIds([]);
              setSelectedUnitId(null);
              setSelectedEntityId(entityId);
            }}
            onSelectedTeamIdsChange={handleOrganizationFilterChange}
            onSelectedUnitIdChange={handleUnitFilterChange}
          />
          <PrimaryButton
            size="large"
            iconName="v2/plus"
            label={t("OrganizationSettings.CreateUnit")}
            disabled={organization.isLoading || organization.error != null}
            onClick={() =>
              setCreateTarget({ kind: "unit", entityId: activeEntityId })
            }
          />
        </UnitControls>
      )}

      {organization.error && entities.length > 0 && (
        <ErrorRow>
          <ErrorBox>{t("OrganizationSettings.LoadFailed")}</ErrorBox>
          <SecondaryButton
            size="small"
            label={t("OrganizationSettings.Retry")}
            onClick={() => void organization.reload()}
          />
        </ErrorRow>
      )}
      {organization.error && entities.length === 0 ? (
        <EmptyState>
          <ErrorBox>{t("OrganizationSettings.LoadFailed")}</ErrorBox>
          <SecondaryButton
            size="small"
            label={t("OrganizationSettings.Retry")}
            onClick={() => void organization.reload()}
          />
        </EmptyState>
      ) : organization.isLoading && entities.length === 0 ? (
        <LoadingText>{t("OrganizationSettings.Loading")}</LoadingText>
      ) : entities.length === 0 ? (
        <EmptyState>
          <EmptyTitle>{t("OrganizationSettings.NoEntities")}</EmptyTitle>
          <EmptyText>{t("OrganizationSettings.NoEntitiesInfo")}</EmptyText>
          <PrimaryButton
            size="small"
            iconName="v2/plus"
            label={t("OrganizationSettings.CreateEntity")}
            onClick={() => setCreateTarget({ kind: "entity" })}
          />
        </EmptyState>
      ) : activeEntityId == null ? null : visibleUnits.length > 0 ? (
        <UnitList>{visibleUnits.map(renderUnit)}</UnitList>
      ) : (
        <EmptyState>
          <EmptyTitle>
            {hasOrganizationFilter
              ? t("OrganizationSettings.NoSearchResults")
              : t("OrganizationSettings.NoUnits")}
          </EmptyTitle>
          <EmptyText>
            {hasOrganizationFilter
              ? t("OrganizationSettings.NoSearchResultsInfo")
              : t("OrganizationSettings.NoUnitsInfo")}
          </EmptyText>
          {!hasOrganizationFilter && (
            <PrimaryButton
              size="small"
              iconName="v2/plus"
              label={t("OrganizationSettings.CreateUnit")}
              onClick={() =>
                setCreateTarget({
                  kind: "unit",
                  entityId: activeEntityId,
                })
              }
            />
          )}
        </EmptyState>
      )}

      {createTarget != null && (
        <OrganizationCreateDialog
          kind={createTarget.kind}
          parentName={
            createTarget.kind === "team" ? createTarget.unitName : undefined
          }
          isOpen
          onClose={() => setCreateTarget(null)}
          onSave={(name) => {
            if (createTarget.kind === "team") {
              return organization.createTeam({
                name,
                unitId: createTarget.unitId,
              });
            }
            if (createTarget.kind === "unit") {
              return organization.createUnit({
                entityId: createTarget.entityId,
                name,
              });
            }
            return organization.createEntity(name);
          }}
        />
      )}
      <OrganizationEditPanel
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSave={saveEdit}
        onDelete={deleteEdit}
      />
      <OrganizationMembersPanel
        teamId={membersTeamId}
        onClose={() => setMembersTeamId(null)}
        loadMembers={organization.loadTeamMembers}
        loadWorkers={organization.loadCompanyWorkers}
        assignWorker={organization.assignWorker}
        removeWorker={organization.removeWorker}
      />
    </Page>
  );
};
